import React, { useState, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import { AddCommaNumber } from '../../../helpers/Utils'

// import annotationPlugin from 'chartjs-plugin-annotation'; // Import plugin annotation
import {
  Container, Row, Col, Card, CardHeader, CardBody, CardTitle, CardText,
  Form, FormGroup, Label, Input, Button, Table, Alert
} from 'reactstrap';
import './BreakEvenAnalysis.css'; // Import CSS cho component

// Đăng ký các thành phần của Chart.js và plugin annotation một lần
// Chart.register(...registerables, annotationPlugin);

const BreakEvenAnalysis = () => {
  const initialInputs = {
    period: `Tháng ${new Date().getMonth() + 1}/${new Date().getFullYear()}`,
    rentCost: 25000000,
    fixedSalaryCost: 30000000,
    equipmentDepreciation: 5000000,
    managementCost: 5000000,
    otherFixedCost: 3000000,
    unitPrice: 100000,
    unitVariableCost: 40000,
  };

  const [inputs, setInputs] = useState(initialInputs);
  const [results, setResults] = useState({
    totalFixedCost: 0,
    contributionMargin: 0,
    contributionRatio: 0,
    breakEvenUnits: 0,
    breakEvenRevenue: 0,
    detailTable: [],
    calculated: false, // Thêm cờ để biết đã tính toán hay chưa
  });
  const [error, setError] = useState('');

  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs({
      ...inputs,
      [name]: name === 'period' ? value : parseFloat(value) || 0, // Giữ nguyên period là string, còn lại parse
    });
    console.log(name, value);
    console.log('inputs', inputs);
    setError(''); // Xóa lỗi khi người dùng thay đổi input
  };

  const validateInputs = () => {
    if (inputs.unitPrice <= 0) {
      setError('Giá bán trung bình phải lớn hơn 0.');
      return false;
    }
    if (inputs.unitVariableCost < 0) {
      setError('Chi phí biến đổi không được âm.');
      return false;
    }
    if (inputs.unitPrice <= inputs.unitVariableCost) {
      setError('Giá bán phải lớn hơn chi phí biến đổi đơn vị.');
      return false;
    }
    // Thêm các validate khác nếu cần
    return true;
  };

  const calculateResults = () => {
    if (!validateInputs()) {
      // Xóa biểu đồ và bảng nếu có lỗi
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
      setResults(prev => ({ ...prev, detailTable: [], calculated: false }));
      return;
    }

    const totalFixedCost =
      inputs.rentCost +
      inputs.fixedSalaryCost +
      inputs.equipmentDepreciation +
      inputs.managementCost +
      inputs.otherFixedCost;

    const contributionMargin = inputs.unitPrice - inputs.unitVariableCost;
    const contributionRatio = (contributionMargin / inputs.unitPrice) * 100;
    const breakEvenUnits = totalFixedCost / contributionMargin;
    const breakEvenRevenue = breakEvenUnits * inputs.unitPrice;
    const detailTable = generateDetailTable(totalFixedCost, breakEvenUnits, inputs.unitPrice, inputs.unitVariableCost);

    setResults({
      totalFixedCost,
      contributionMargin,
      contributionRatio,
      breakEvenUnits,
      breakEvenRevenue,
      detailTable,
      calculated: true,
    });

    drawBreakEvenChart(totalFixedCost, breakEvenUnits, inputs.unitPrice, inputs.unitVariableCost);
  };

  const handleSubmit = (e) => {
    e.preventDefault(); // Ngăn form submit mặc định
    calculateResults();
  };

  // Hàm tạo dữ liệu bảng chi tiết (cập nhật để nhận thêm params)
  const generateDetailTable = (totalFixedCost, beUnits, unitPrice, unitVariableCost) => {
    const table = [];
    // Điều chỉnh các mốc sản lượng cho hợp lý hơn
    const volumePoints = [
      0,
      Math.max(0, Math.floor(beUnits * 0.25)),
      Math.max(0, Math.floor(beUnits * 0.5)),
      Math.max(0, Math.floor(beUnits * 0.75)),
      Math.ceil(beUnits), // Điểm hòa vốn
      Math.ceil(beUnits * 1.25),
      Math.ceil(beUnits * 1.5),
      Math.ceil(beUnits * 1.75)
    ].filter((v, i, a) => a.indexOf(v) === i).sort((a,b) => a-b); // Loại bỏ trùng lặp và sắp xếp

    if (beUnits === Infinity || isNaN(beUnits)) { // Xử lý trường hợp không tính được điểm hòa vốn
        return [];
    }

    volumePoints.forEach(volume => {
      const revenue = volume * unitPrice;
      const variableCost = volume * unitVariableCost;
      const totalCost = totalFixedCost + variableCost;
      const profit = revenue - totalCost;
      table.push({ volume, revenue, variableCost, fixedCost: totalFixedCost, totalCost, profit });
    });
    return table;
  };

  // Hàm vẽ biểu đồ (cập nhật để nhận thêm params)
  const drawBreakEvenChart = (totalFixedCost, beUnits, unitPrice, unitVariableCost) => {
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }
    if (!chartRef.current || beUnits === Infinity || isNaN(beUnits)) return; // Không vẽ nếu không có canvas hoặc BEP không hợp lệ

    const maxUnits = Math.max(10, Math.ceil(beUnits * 1.8)); // Đảm bảo có ít nhất 10 đơn vị trên trục x
    const units = Array.from({ length: Math.floor(maxUnits / Math.max(1, Math.floor(maxUnits/20))) + 1 }, (_, i) => i * Math.max(1, Math.floor(maxUnits/20)));
    if (units[units.length-1] < maxUnits && units.length > 1) units.push(maxUnits); // Đảm bảo điểm cuối cùng được bao gồm


    const revenueData = units.map(volume => volume * unitPrice);
    const fixedCostData = units.map(() => totalFixedCost);
    const totalCostData = units.map(volume => totalFixedCost + volume * unitVariableCost);
    const profitData = units.map((volume, index) => revenueData[index] - totalCostData[index]);

    const ctx = chartRef.current.getContext('2d');
    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: units,
        datasets: [
          {
            label: 'Doanh thu (TR)',
            data: revenueData,
            borderColor: 'rgb(75, 192, 192)',
            tension: 0.1,
            fill: false,
          },
          {
            label: 'Tổng chi phí (TC)',
            data: totalCostData,
            borderColor: 'rgb(255, 99, 132)',
            tension: 0.1,
            fill: false,
          },
          {
            label: 'Chi phí cố định (FC)',
            data: fixedCostData,
            borderColor: 'rgb(255, 159, 64)',
            borderDash: [5, 5],
            tension: 0.1,
            fill: false,
          },
          //   { // Có thể ẩn bớt đường lợi nhuận nếu muốn biểu đồ đơn giản hơn
          //     label: 'Lợi nhuận',
          //     data: profitData,
          //     borderColor: 'rgb(153, 102, 255)',
          //     tension: 0.1,
          //     fill: false
          //   }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index',
        },
        scales: {
          x: {
            title: { display: true, text: 'Số lượng sản phẩm (Q)' },
            beginAtZero: true
          },
          y: {
            title: { display: true, text: 'Giá trị (VNĐ)' },
            ticks: { callback: value => formatCurrency(value) },
            beginAtZero: true
          }
        },
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'Biểu Đồ Phân Tích Điểm Hòa Vốn'
          },
          tooltip: {
            callbacks: {
              label: context => `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`
            }
          },
          annotation: { // Đảm bảo plugin annotation đã được import và register
            annotations: {
              breakEvenLine: {
                type: 'line',
                scaleID: 'x',
                value: beUnits,
                borderColor: 'red',
                borderWidth: 2,
                borderDash: [6, 6],
                label: {
                  content: `Hòa vốn: ${Math.round(beUnits)} SP`,
                  enabled: true,
                  position: 'start',
                  backgroundColor: 'rgba(255,0,0,0.7)',
                  color: 'white',
                  font: { weight: 'bold' }
                }
              },
              breakEvenRevenueLine: { // Thêm đường ngang cho doanh thu hòa vốn
                type: 'line',
                scaleID: 'y',
                value: beUnits * unitPrice,
                borderColor: 'red',
                borderWidth: 2,
                borderDash: [6, 6],
                display: (ctx) => ctx.chart.scales.x.getValueForPixel(ctx.chart.width) > beUnits && beUnits > 0, // Chỉ hiển thị nếu điểm hòa vốn nằm trong biểu đồ
                label: {
                  content: `DT hòa vốn: ${formatCurrency(beUnits * unitPrice)}`,
                  enabled: true,
                  position: 'end',
                  backgroundColor: 'rgba(255,0,0,0.7)',
                  color: 'white',
                  font: { weight: 'bold' }
                }
              }
            }
          }
        }
      }
    });
  };

  const formatCurrency = (value) => {
    if (isNaN(value) || value === null) return 'N/A';
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value, digits = 0) => {
    if (isNaN(value) || value === null) return 'N/A';
    return new Intl.NumberFormat('vi-VN', { maximumFractionDigits: digits }).format(value);
  }

  // useEffect để tính toán lần đầu khi component mount (nếu muốn)
  // Hoặc để trống để chỉ tính khi bấm submit
  // useEffect(() => {
  //   calculateResults();
  // }, []); // Bỏ inputs dependencia để không tự tính khi input thay đổi

  return (
    <Container fluid className="break-even-analysis p-4 bg-light">
      <Card className="mb-4 shadow-sm">
        <CardHeader className="bg-primary text-white">
          <h1 className="mb-0 h3 mt-4">Phân Tích Điểm Hòa Vốn</h1>
        </CardHeader>
      </Card>

      {error && <Alert color="danger" className="mt-3">{error}</Alert>}

      <Row>
        <Col md="5">
          <Card className="mb-4 shadow-sm">
            {/* <CardHeader>
              <h2 className="h5 mb-0 mt-4">Thông Số Đầu Vào</h2>
            </CardHeader> */}
            <CardBody>
              <Form onSubmit={handleSubmit}>
                {/* <FormGroup>
                  <Label for="period">Thời kỳ phân tích</Label>
                  <Input type="text" name="period" id="period" value={inputs.period} onChange={handleInputChange} />
                </FormGroup> */}

                <h3 className="h6 mb-4 text-primary">Chi Phí Cố Định (VNĐ)</h3>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label for="rentCost">Tiền thuê mặt bằng</Label>
                      <Input type="number" name="rentCost" id="rentCost" value={inputs.rentCost} onChange={handleInputChange} placeholder={AddCommaNumber(inputs.rentCost)} />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="fixedSalaryCost">Lương nhân viên cố định</Label>
                      <Input type="number" name="fixedSalaryCost" id="fixedSalaryCost" value={inputs.fixedSalaryCost} onChange={handleInputChange} placeholder={AddCommaNumber(inputs.fixedSalaryCost)} />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label for="equipmentDepreciation">Khấu hao thiết bị</Label>
                      <Input type="number" name="equipmentDepreciation" id="equipmentDepreciation" value={inputs.equipmentDepreciation} onChange={handleInputChange} placeholder={AddCommaNumber(inputs.equipmentDepreciation)} />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="managementCost">Quản lý & Marketing</Label>
                      <Input type="number" name="managementCost" id="managementCost" value={inputs.managementCost} onChange={handleInputChange} placeholder={AddCommaNumber(inputs.managementCost)} />
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup>
                  <Label for="otherFixedCost">Chi phí cố định khác</Label>
                  <Input type="number" name="otherFixedCost" id="otherFixedCost" value={inputs.otherFixedCost} onChange={handleInputChange} placeholder={AddCommaNumber(inputs.otherFixedCost)} />
                </FormGroup>

                <h3 className="h6 my-4 text-primary">Thông Tin Sản Phẩm</h3>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label for="unitPrice">Giá bán trung bình/sản phẩm (VNĐ)</Label>
                      <Input type="number" name="unitPrice" id="unitPrice" value={inputs.unitPrice} onChange={handleInputChange} placeholder={AddCommaNumber(inputs.unitPrice)} />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="unitVariableCost">Giá vốn trung bình/sản phẩm (VNĐ)</Label>
                      <Input type="number" name="unitVariableCost" id="unitVariableCost" value={inputs.unitVariableCost} onChange={handleInputChange} placeholder={AddCommaNumber(inputs.unitVariableCost)} />
                    </FormGroup>
                  </Col>
                </Row>
                <Button type="submit" block className="mt-3" color="primary">
                  <i className="bi bi-calculator-fill me-2"></i> Phân Tích
                </Button>
              </Form>
            </CardBody>
          </Card>
        </Col>

        <Col md="7">
          <Card className="mb-4 shadow-sm">
            <CardHeader>
              <h2 className="h5 mb-0 mt-4">Kết Quả Phân Tích</h2>
            </CardHeader>
            <CardBody>
              {!results.calculated && !error && (
                <Alert color="info">Vui lòng nhập thông số và nhấn "Phân Tích" để xem kết quả.</Alert>
              )}
              {results.calculated && !error && (
                <>
                  <Row>
                    <Col sm="6" className="mb-3">
                      <Card body className="text-center h-100 bg-light">
                        <CardTitle tag="h6" className="text-muted">Tổng Chi Phí Cố Định</CardTitle>
                        <CardText className="h4 fw-bold text-danger">{formatCurrency(results.totalFixedCost)}</CardText>
                      </Card>
                    </Col>
                    <Col sm="6" className="mb-3">
                      <Card body className="text-center h-100 bg-light">
                        <CardTitle tag="h6" className="text-muted">Biên Đóng Góp / SP</CardTitle>
                        <CardText className="h4 fw-bold text-success">{formatCurrency(results.contributionMargin)}</CardText>
                      </Card>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm="6" className="mb-3">
                      <Card body className="text-center h-100 alert-warning">
                        <CardTitle tag="h5">Điểm Hòa Vốn (Số Lượng)</CardTitle>
                        <CardText className="h3 fw-bolder">{formatNumber(results.breakEvenUnits)} SP</CardText>
                      </Card>
                    </Col>
                    <Col sm="6" className="mb-3">
                      <Card body className="text-center h-100 alert-info">
                        <CardTitle tag="h5">Điểm Hòa Vốn (Doanh Thu)</CardTitle>
                        <CardText className="h3 fw-bolder">{formatCurrency(results.breakEvenRevenue)}</CardText>
                      </Card>
                    </Col>
                  </Row>
                  <Table bordered size="sm" className="mt-3 additional-info-table">
                    <tbody>
                      <tr>
                        <td>Tỷ lệ đóng góp (%)</td>
                        <td className="fw-bold">{formatNumber(results.contributionRatio, 2)}%</td>
                      </tr>
                      {/* Các chỉ số khác có thể thêm sau */}
                    </tbody>
                  </Table>
                </>
              )}
            </CardBody>
          </Card>

          {/* {results.calculated && !error && (
            <Card className="mb-4 shadow-sm">
              <CardHeader>
                <h2 className="h5 mb-0 mt-4">Biểu Đồ Điểm Hòa Vốn</h2>
              </CardHeader>
              <CardBody>
                <div className="chart-container" style={{ height: '400px', position: 'relative' }}>
                  <canvas ref={chartRef}></canvas>
                </div>
              </CardBody>
            </Card>
          )} */}
        </Col>
      </Row>

      {results.calculated && !error && results.detailTable.length > 0 && (
        <Row>
          <Col>
            <Card className="mb-4 shadow-sm">
              <CardHeader>
                <h2 className="h5 mb-0 mt-4">Bảng Chi Tiết Phân Tích Lợi Nhuận</h2>
              </CardHeader>
              <CardBody>
                <div className="table-responsive">
                  <Table striped bordered hover size="sm" className="text-center">
                    <thead className="table-primary">
                      <tr>
                        <th>Số Lượng (SP)</th>
                        <th>Doanh Thu (VNĐ)</th>
                        <th>Chi Phí Biến Đổi (VNĐ)</th>
                        <th>Chi Phí Cố Định (VNĐ)</th>
                        <th>Tổng Chi Phí (VNĐ)</th>
                        <th>Lợi Nhuận (VNĐ)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.detailTable.map((row, index) => (
                        <tr key={index} className={row.volume === Math.ceil(results.breakEvenUnits) ? 'break-even-row table-warning fw-bold' : ''}>
                          <td>{formatNumber(row.volume)}</td>
                          <td>{formatCurrency(row.revenue)}</td>
                          <td>{formatCurrency(row.variableCost)}</td>
                          <td>{formatCurrency(row.fixedCost)}</td>
                          <td>{formatCurrency(row.totalCost)}</td>
                          <td className={row.profit > 0 ? 'text-success' : row.profit < 0 ? 'text-danger' : ''}>
                            {formatCurrency(row.profit)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      <Card className="shadow-sm">
        <CardHeader>
          <h2 className="h5 mb-0 mt-4">Hướng Dẫn và Giải Thích</h2>
        </CardHeader>
        <CardBody>
          <Row>
            <Col md="6">
              <h5>Cách Tính Điểm Hòa Vốn</h5>
              <p>Điểm hòa vốn là điểm mà tại đó tổng doanh thu bằng tổng chi phí, tức là lợi nhuận bằng không.</p>
              <ul>
                <li><strong>Điểm hòa vốn (số lượng sản phẩm):</strong> <br />
                  <code>Tổng chi phí cố định / (Giá bán đơn vị - Chi phí biến đổi đơn vị)</code></li>
                <li><strong>Điểm hòa vốn (doanh thu):</strong> <br />
                  <code>Điểm hòa vốn (số lượng) × Giá bán đơn vị</code></li>
              </ul>
            </Col>
            <Col md="6">
              <h5>Các Chỉ Số Quan Trọng</h5>
              <ul>
                <li><strong>Tổng chi phí cố định (Fixed Costs - FC):</strong> Chi phí không thay đổi theo sản lượng (ví dụ: thuê nhà, lương cố định).</li>
                <li><strong>Chi phí biến đổi đơn vị (Variable Cost per Unit - VC/u):</strong> Chi phí thay đổi trực tiếp theo mỗi đơn vị sản phẩm (ví dụ: nguyên vật liệu).</li>
                <li><strong>Biên đóng góp đơn vị (Contribution Margin per Unit):</strong> <br/> <code>Giá bán đơn vị - Chi phí biến đổi đơn vị</code>. <br />Phần giá bán còn lại sau khi trừ chi phí biến đổi, dùng để bù đắp chi phí cố định và tạo lợi nhuận.</li>
                <li><strong>Tỷ lệ đóng góp (Contribution Margin Ratio):</strong> <br /> <code>(Biên đóng góp đơn vị / Giá bán đơn vị) × 100%</code>.</li>
              </ul>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </Container>
  );
};

export default BreakEvenAnalysis;